import React, { useCallback, useEffect, useState } from 'react';
import {
  listAccountGroups, createAccountGroup, assignAccountGroupMember, deleteAccountGroup,
  adminListUsers, listAccountGroupMembers,
  AccountGroupWithCount, AccountGroupMember,
} from '../../api';
import { User } from '../../types';
import { formatDate } from '../../utils/formatDate';
import Breadcrumb from '../../components/Breadcrumb';
import EmptyState from '../../components/EmptyState';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';

const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const th: React.CSSProperties = {
  padding: '0.7rem 0.9rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600,
  letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)',
};
const td: React.CSSProperties = { padding: '0.7rem 0.9rem', fontSize: '0.88rem', color: 'var(--text)' };

const AccountGroups: React.FC = () => {
  const toast = useToast();
  const [groups, setGroups] = useState<AccountGroupWithCount[]>([]);
  const [newName, setNewName] = useState('');
  const [assignGroupId, setAssignGroupId] = useState('');
  const [assignUsername, setAssignUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [assignAck, setAssignAck] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<{ groupId: string; username: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AccountGroupWithCount | null>(null);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // 成员明细懒加载:展开的组 id + 已加载成员缓存 + 加载中集合
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [membersCache, setMembersCache] = useState<Record<string, AccountGroupMember[]>>({});
  const [membersLoading, setMembersLoading] = useState<string | null>(null);

  const reload = useCallback(() => {
    listAccountGroups().then(r => setGroups(r.groups)).catch(e => toast.error('加载账号组失败: ' + errMsg(e)));
  }, [toast]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const q = assignUsername.trim();
    if (!q) { setSuggestions([]); return; }
    const handle = setTimeout(() => {
      adminListUsers(1, 20, q)
        .then(res => setSuggestions((res.items || []).filter(u => !u.accountGroupId).slice(0, 10)))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [assignUsername]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await createAccountGroup(newName.trim());
      setNewName('');
      toast.success('已创建账号组');
      reload();
    } catch (e: unknown) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doAssign = async () => {
    if (!pendingAssign) return;
    setBusy(true);
    try {
      await assignAccountGroupMember(pendingAssign.groupId, pendingAssign.username);
      toast.success('已划入账号组');
      setAssignUsername('');
      setPendingAssign(null);
      setAssignAck(false);
      // 划入后清掉该组成员缓存,下次展开重新拉取
      setMembersCache(prev => { const next = { ...prev }; delete next[pendingAssign.groupId]; return next; });
      reload();
    } catch (e: unknown) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteAccountGroup(pendingDelete.id);
      toast.success('已删除账号组');
      setPendingDelete(null);
      reload();
    } catch (e: unknown) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleExpand = (groupId: string) => {
    if (expandedId === groupId) { setExpandedId(null); return; }
    setExpandedId(groupId);
    if (!membersCache[groupId]) {
      setMembersLoading(groupId);
      listAccountGroupMembers(groupId)
        .then(r => setMembersCache(prev => ({ ...prev, [groupId]: r.members })))
        .catch(e => toast.error('加载成员失败: ' + errMsg(e)))
        .finally(() => setMembersLoading(null));
    }
  };

  const totalMembers = groups.reduce((sum, g) => sum + g.memberCount, 0);

  return (
    <div className="container">
      <Breadcrumb items={[{ label: '项目', to: '/projects' }, { label: '账号组管理' }]} />
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">账号组管理</h1>
          <p style={{ margin: '0.35rem 0 0', color: 'var(--text-3)', fontSize: '0.88rem' }}>
            将账号划入对账分组，用于月度对账的用量归集。
          </p>
        </div>
      </div>

      {/* 操作卡片:建组 + 划入成员 */}
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.75rem' }}>
          {/* 建组 */}
          <div>
            <div className="section-title" style={{ marginBottom: '0.6rem' }}>新建账号组</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="账号组名称"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newName.trim() && !busy) create(); }}
              />
              <button className="btn btn-primary" disabled={busy || !newName.trim()} onClick={create}>建组</button>
            </div>
          </div>

          {/* 划入成员 */}
          <div>
            <div className="section-title" style={{ marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              划入成员
              <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--danger)', fontSize: '0.72rem' }}>
                划入不可更改或移出
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <select className="input" style={{ minWidth: '9rem' }} value={assignGroupId} onChange={e => setAssignGroupId(e.target.value)}>
                <option value="">选择账号组</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <div style={{ position: 'relative', flex: 1, minWidth: '11rem' }}>
                <input
                  className="input"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  placeholder="搜索用户名(仅未分组)"
                  value={assignUsername}
                  autoComplete="off"
                  onChange={e => { setAssignUsername(e.target.value); setSuggestOpen(true); }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                />
                {suggestOpen && suggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: '220px', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', padding: '0.25rem' }}>
                    {suggestions.map(u => (
                      <div
                        key={u.id}
                        onMouseDown={() => { setAssignUsername(u.username); setSuggestOpen(false); }}
                        style={{ padding: '0.45rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text)', borderRadius: 'var(--radius-sm)' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {u.username}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="btn btn-primary"
                disabled={busy || !assignGroupId || !assignUsername.trim()}
                onClick={() => { setAssignAck(false); setSuggestOpen(false); setPendingAssign({ groupId: assignGroupId, username: assignUsername.trim() }); }}
              >
                划入
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 列表卡片 */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div className="section-title" style={{ margin: 0 }}>账号组列表</div>
          {groups.length > 0 && (
            <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
              {groups.length} 个组 · {totalMembers} 名成员
            </span>
          )}
        </div>

        {groups.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>账号组</th>
                  <th style={{ ...th, textAlign: 'center', width: '7rem' }}>成员数</th>
                  <th style={{ ...th, textAlign: 'right', width: '6rem' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => {
                  const expanded = expandedId === g.id;
                  return (
                    <React.Fragment key={g.id}>
                      <tr
                        style={{ borderBottom: expanded ? 'none' : '1px solid var(--border)', background: expanded ? 'var(--primary-light)' : 'transparent', transition: 'background var(--transition-fast)' }}
                      >
                        <td style={{ ...td, padding: 0 }}>
                          <button
                            onClick={() => toggleExpand(g.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', width: '100%', padding: '0.7rem 0.9rem', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', color: 'var(--text)', fontWeight: 600, textAlign: 'left' }}
                          >
                            <span style={{ display: 'inline-flex', color: 'var(--text-3)', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform var(--transition-fast)' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                            </span>
                            {g.name}
                          </button>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ display: 'inline-block', minWidth: '1.6rem', padding: '0.12rem 0.55rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, background: g.memberCount > 0 ? 'var(--primary-light)' : 'var(--bg-2)', color: g.memberCount > 0 ? 'var(--primary)' : 'var(--text-3)' }}>
                            {g.memberCount}
                          </span>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: g.memberCount > 0 ? 'var(--text-3)' : 'var(--danger)' }}
                            disabled={busy || g.memberCount > 0}
                            title={g.memberCount > 0 ? '组内有成员,不可删除' : '删除空组'}
                            onClick={() => setPendingDelete(g)}
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan={3} style={{ padding: '0 0.9rem 0.9rem' }}>
                            <div style={{ background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: membersLoading === g.id || !membersCache[g.id]?.length ? '0.9rem' : '0.35rem 0.9rem' }}>
                              {membersLoading === g.id ? (
                                <span style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>加载中…</span>
                              ) : (membersCache[g.id]?.length ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ ...th, padding: '0.5rem 0.5rem' }}>用户名</th>
                                      <th style={{ ...th, padding: '0.5rem 0.5rem' }}>手机号</th>
                                      <th style={{ ...th, padding: '0.5rem 0.5rem' }}>创建时间</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {membersCache[g.id].map(m => (
                                      <tr key={m.id}>
                                        <td style={{ ...td, padding: '0.5rem 0.5rem', fontWeight: 500 }}>{m.username}</td>
                                        <td style={{ ...td, padding: '0.5rem 0.5rem', color: 'var(--text-2)' }}>{m.phone || '-'}</td>
                                        <td style={{ ...td, padding: '0.5rem 0.5rem', color: 'var(--text-3)', fontSize: '0.82rem' }}>{formatDate(m.createdAt)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <span style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>该组暂无成员</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="&#128193;" description="暂无账号组，先在上方新建一个" />
        )}
      </div>
      </div>

      {/* 划入二次确认 */}
      <Modal
        open={!!pendingAssign}
        onClose={() => { setPendingAssign(null); setAssignAck(false); }}
        title="确认划入账号组"
        width="420px"
        footer={
          <>
            <button className="btn btn-secondary" disabled={busy} onClick={() => { setPendingAssign(null); setAssignAck(false); }}>取消</button>
            <button className="btn btn-danger" disabled={busy || !assignAck} onClick={doAssign}>
              {busy && <span className="loading-spinner" />}确认划入
            </button>
          </>
        }
      >
        <div style={{ fontSize: '0.9rem', color: 'var(--text-2)', lineHeight: 1.6 }}>
          <div style={{ textAlign: 'center', padding: '0.25rem 0 1rem' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--primary)' }}>{pendingAssign?.username}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-3)', margin: '0.35rem 0' }}>划入分组</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--primary)' }}>
              {groups.find(g => g.id === pendingAssign?.groupId)?.name || pendingAssign?.groupId}
            </div>
          </div>
          <p style={{ marginTop: 0 }}>账号一旦划入,将<strong style={{ color: 'var(--danger)' }}>永久锁定在该组,不可更改或移出</strong>。请确认无误。</p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.75rem' }}>
            <input type="checkbox" checked={assignAck} onChange={e => setAssignAck(e.target.checked)} />
            我已知悉此操作不可逆
          </label>
        </div>
      </Modal>

      {/* 删除确认 */}
      <ConfirmDialog
        open={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={doDelete}
        title="删除账号组"
        message={`确认删除空组「${pendingDelete?.name ?? ''}」?`}
        confirmText="删除"
        confirmDanger
        loading={busy}
      />
    </div>
  );
};

export default AccountGroups;
